/**
 * Why a job view that arrived with a utm_source was not handed off to the
 * employer's form. Stored on the analytics view row as
 * `auto_redirect_skip_reason`, so a missing hand-off can be queried rather than
 * inferred from absent rows.
 *
 * The column is a plain varchar, not a Postgres enum: adding a code here needs
 * no migration. Keep existing codes stable - they are what past rows hold.
 */
export const AutomaticApplySkipReason = {
  ALREADY_HANDED_OFF: "already_handed_off",
  SESSION_STORAGE_UNAVAILABLE: "session_storage_unavailable",
  BLANK_UTM_SOURCE: "blank_utm_source",
  CONFIG_FETCH_TIMEOUT: "config_fetch_timeout",
  CONFIG_FETCH_FAILED: "config_fetch_failed",
  CONFIG_HTTP_ERROR: "config_http_error",
  CONFIG_UPSTREAM_ERROR: "config_upstream_error",
  CONFIG_INVALID_JSON: "config_invalid_json",
  CONFIG_MALFORMED: "config_malformed",
  EXCLUDED_USER_AGENT: "excluded_user_agent",
  EMAIL_APPLICATION: "email_application",
  APPLY_URL_NOT_NAVIGABLE: "apply_url_not_navigable",
  ORGANIZATION_NOT_CONFIGURED: "organization_not_configured",
  UTM_SOURCE_NOT_CONFIGURED: "utm_source_not_configured",
  EVALUATION_ERROR: "evaluation_error",
  HANDOFF_ERROR: "handoff_error",
  UNKNOWN: "unknown",
} as const;

export type AutomaticApplySkipReasonCode =
  (typeof AutomaticApplySkipReason)[keyof typeof AutomaticApplySkipReason];

export interface IAutomaticApplySkip {
  reason: AutomaticApplySkipReasonCode;
  detail?: string;
}

const MAX_DETAIL_LENGTH = 500;

export function truncateSkipDetail(
  detail: string | undefined
): string | undefined {
  if (detail === undefined) {
    return undefined;
  }
  return detail.length > MAX_DETAIL_LENGTH
    ? detail.slice(0, MAX_DETAIL_LENGTH)
    : detail;
}

export function describeError(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  return String(error);
}
